import { plainText } from '../../lib/plainText';

/**
 * New article (UX optimization review, 2026-10-24): New article ▾ → Write it
 * myself opens the regular article editor, and nothing is created until
 * Create draft. These are the editor's rules for that unsaved article, kept
 * apart so they can be tested.
 */

/** The slug the server would give this title (tinycms/services/slug.js), shown until changed. */
export function slugFromTitle(title: string): string {
  const slug = title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’‘ʼ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');
  return slug || 'article';
}

/** Whether there's anything to lose: a title, a summary or body text. */
export function hasWriting(f: { title: string; summary: string; bodyText: string }): boolean {
  return Boolean(f.title.trim() || plainText(f.summary).trim() || plainText(f.bodyText).trim());
}

interface NewArticleFields {
  title: string;
  summary: string | null;
  body: string | null;
  mediaUrl: string;
  heroAlt: string;
  authorIds: string[];
  showUpdated: boolean;
  /** Sent only when the person changed it from the title's slug; else the server picks a free one. */
  slug: string | null;
  /** The primary collection's slug; '' for none. */
  section?: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  ogImageUrl: string;
  noindex: boolean;
  customFields?: Record<string, unknown>;
}

/** createTinycmsArticle's input: only what was filled in. */
export function newArticleInput(f: NewArticleFields): Record<string, unknown> {
  const seo: Record<string, unknown> = {};
  if (f.metaTitle.trim()) seo.metaTitle = f.metaTitle.trim();
  if (f.metaDescription.trim()) seo.metaDescription = f.metaDescription.trim();
  if (f.canonicalUrl.trim()) seo.canonicalUrl = f.canonicalUrl.trim();
  if (f.ogImageUrl.trim()) seo.ogImageUrl = f.ogImageUrl.trim();
  if (f.noindex) seo.noindex = true;
  const url = f.mediaUrl.trim();
  const fields = Object.entries(f.customFields || {}).filter(([, v]) => v !== null && v !== '');
  return {
    title: f.title.trim(),
    contentType: 'article',
    ...(f.summary ? { summary: f.summary } : {}),
    ...(f.body ? { body: f.body } : {}),
    ...(url ? { hero: { url, ...(f.heroAlt.trim() ? { alt: f.heroAlt.trim() } : {}) } } : {}),
    ...(f.authorIds.length ? { authors: f.authorIds } : {}),
    ...(f.showUpdated ? { showUpdated: true } : {}),
    ...(f.slug ? { slug: f.slug } : {}),
    ...(f.section ? { section: f.section } : {}),
    ...(Object.keys(seo).length ? { seo } : {}),
    ...(fields.length ? { customFields: Object.fromEntries(fields) } : {}),
  };
}
